import { ATTENDANCE_URL } from "../constants";
import { apiSlice } from "./apiSlice";

export const attendanceApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Clock In/Out - POST with form-data
    clockInOut: builder.mutation({
      query: (formData) => {
        // formData should be a FormData object
        // RTK Query automatically detects FormData and doesn't set Content-Type
        // The browser will set it with the proper boundary for multipart/form-data
        return {
          url: ATTENDANCE_URL + "/attendance/clock",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: [
        "Attendance",
        "AttendanceHistory",
        "AttendanceStatistics",
      ],
    }),

    // Get attendance history by date range
    getAttendanceHistory: builder.query({
      query: ({ start_date, end_date }) => {
        const params = new URLSearchParams();
        if (start_date) params.append("start_date", start_date);
        if (end_date) params.append("end_date", end_date);
        return {
          url: `${ATTENDANCE_URL}/attendance/history/paginated?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["AttendanceHistory"],
    }),

    // Get entries for a specific attendance ID
    getAttendanceEntries: builder.query({
      query: (attendanceId) => ({
        url: `${ATTENDANCE_URL}/attendance/${attendanceId}/entries`,
        method: "GET",
      }),
      providesTags: (result, error, attendanceId) => [
        { type: "AttendanceEntries", id: attendanceId },
      ],
    }),

    // Get entries for a specific attendance ID (admin/CFM endpoint for cross-role viewing)
    // getAdminAttendanceEntries: builder.query({
    //   query: (attendanceId) => ({
    //     url: `${ATTENDANCE_URL}/attendance/${attendanceId}/entries`,
    //     method: "GET",
    //   }),
    //   providesTags: (result, error, attendanceId) => [
    //     { type: "AttendanceEntries", id: attendanceId },
    //   ],
    // }),

    // Get attendance statistics
    getAttendanceStatistics: builder.query({
      query: () => ({
        url: ATTENDANCE_URL + "/attendance/statistic",
        method: "GET",
      }),
      providesTags: ["AttendanceStatistics"],
    }),

    // Get today's attendance record (helper query that uses history with today's date)
    getTodayAttendance: builder.query({
      query: () => {
        const today = new Date().toISOString().split("T")[0];
        return {
          url: `${ATTENDANCE_URL}/attendance/history/paginated?start_date=${today}&end_date=${today}`,
          method: "GET",
        };
      },
      transformResponse: (response) => {
        // Return the first record if available, or null
        if (Array.isArray(response) && response.length > 0) {
          return response[0];
        }
        return null;
      },
      providesTags: ["Attendance"],
    }),

    // Get attendance by date (for checking today's attendance with entries)
    getAttendanceByDate: builder.query({
      query: (date) => ({
        url: `${ATTENDANCE_URL}/attendance/date/${date}`,
        method: "GET",
      }),
      providesTags: ["Attendance"],
    }),

    // Get daily attendance report (for supervisor/manager views)
    getDailyAttendanceReport: builder.query({
      query: ({ report_type, status, academyId, page = 0, size = 20 }) => {
        const params = new URLSearchParams();
        if (report_type) params.append("report_type", report_type);
        if (status) params.append("status", status);
        if (academyId) params.append("academyId", academyId);
        params.append("page", page.toString());
        params.append("size", size.toString());
        return {
          url: `${ATTENDANCE_URL}/attendance/daily-report?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["AttendanceHistory"],
    }),

    // Get academy statistics (for CFM views)
    getAcademyStatistics: builder.query({
      query: ({ academyId, date }) => {
        const params = new URLSearchParams();
        if (academyId) params.append("academyId", academyId);
        if (date) params.append("date", date);
        const queryString = params.toString();
        return {
          url: `${ATTENDANCE_URL}/attendance/academy-statistics${
            queryString ? `?${queryString}` : ""
          }`,
          method: "GET",
        };
      },
      providesTags: ["AttendanceStatistics"],
    }),

    // Get admin statistics (for Admin/SuperAdmin views)
    getAdminStatistics: builder.query({
      query: ({ date }) => {
        const params = new URLSearchParams();
        if (date) params.append("date", date);
        const queryString = params.toString();
        return {
          url: `${ATTENDANCE_URL}/attendance/admin/statistics${
            queryString ? `?${queryString}` : ""
          }`,
          method: "GET",
        };
      },
      providesTags: ["AttendanceStatistics"],
    }),

    // Get admin daily report (for Admin/SuperAdmin views)
    getAdminDailyReport: builder.query({
      query: ({ status, page = 0, size = 20, academyId }) => {
        const params = new URLSearchParams();
        if (status) params.append("status", status);
        params.append("page", page.toString());
        params.append("size", size.toString());
        if (academyId && academyId !== "all")
          params.append("academyId", academyId);
        return {
          url: `${ATTENDANCE_URL}/attendance/admin/daily-report?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["AttendanceHistory"],
    }),

    // Get admin attendance report for date range
    getAdminAttendanceReport: builder.query({
      query: ({ start_date, end_date, academyId }) => {
        const params = new URLSearchParams();
        if (start_date) params.append("start_date", start_date);
        if (end_date) params.append("end_date", end_date);
        if (academyId && academyId !== "all")
          params.append("academyId", academyId);
        return {
          url: `${ATTENDANCE_URL}/attendance/admin/report?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["AttendanceHistory"],
    }),

    // Get attendance records by userId (for user details page)
    getAttendanceByUserId: builder.query({
      query: (userId) => ({
        url: `${ATTENDANCE_URL}/attendance/user/${userId}`,
        method: "GET",
      }),
      providesTags: ["AttendanceHistory"],
    }),
  }),
});

export const {
  useClockInOutMutation,
  useGetAttendanceHistoryQuery,
  useGetAttendanceEntriesQuery,
  useGetAttendanceStatisticsQuery,
  useGetTodayAttendanceQuery,
  useGetAttendanceByDateQuery,
  useGetDailyAttendanceReportQuery,
  useGetAcademyStatisticsQuery,
  useGetAdminStatisticsQuery,
  useGetAdminDailyReportQuery,
  useGetAdminAttendanceReportQuery,
  useLazyGetAdminAttendanceReportQuery,
  useGetAttendanceByUserIdQuery,
} = attendanceApiSlice;
